import {FC, MouseEvent, useCallback, useMemo} from "react";
import {noop} from "lodash";

import {HeaderDropDown} from "components";
import {customConfirmAlert} from "helpers";
import {useAppDispatch, useAppSelector} from "hooks";
import {HeaderProjectIcon, MarketMapsIcon, PreviewIcon, RecycleBinLine} from "assets/icons";
import type {TFeatureProp} from "components/HeaderDropDown/types";
import {projectSelector} from "store/project-list/projectListSelectors";
import {
	removeCompaniesFromTargetList,
	removeCompanyFromTargetList,
} from "store/target-list/targetListAsyncActions";
import {
	addingCompanyToTargetListInProgressSelector,
	removingCompaniesFromTargetListInProgressSelector,
	removingCompanyFromTargetListInProgressSelector,
	savingCompaniesByFilterInProgressSelector,
	targetCompaniesPaginationSelector,
	targetListCompaniesSelector,
	targetListCompanyInProgressSelector,
	targetListsInProgressSelector,
} from "store/target-list/targetListSelectors";
import {
	hasAccessToMarketMapsSelector,
	isSuperUserSelector,
	userLicenseTypeNameSelector,
} from "store/user/userSelectors";
import {addFromMarketMapModal} from "store/market-map/marketMapActions";

import type {TSavedCompanyProp} from "./types";

import styles from "../header.module.scss";
import {STCompany} from "api/companyApi/types";
import {setProjectTabIndex} from "store/project-list/projectListActions";
import {useHistory} from "react-router-dom";
import ProjectTabs from "mapx-pages/Project/ProjectTabs";
import {useSelector} from "react-redux";

const SavedCompany: FC<TSavedCompanyProp> = ({className}) => {
	const dispatch = useAppDispatch();

	const history = useHistory();

	const project = useAppSelector(projectSelector);

	const companies = useAppSelector(targetListCompaniesSelector);

	const targetCompanyPagination = useAppSelector(targetCompaniesPaginationSelector);

	const userLicenseTypeName = useAppSelector(userLicenseTypeNameSelector);

	const loadingTargetLists = useAppSelector(targetListsInProgressSelector);

	const isSuperUSer = useAppSelector(isSuperUserSelector);

	const hasAccessToMarketMaps = useAppSelector(hasAccessToMarketMapsSelector);

	const loadingCompanyTargetList = useAppSelector(targetListCompanyInProgressSelector);

	const addingCompany = useAppSelector(addingCompanyToTargetListInProgressSelector);

	const removingCompanies = useAppSelector(removingCompaniesFromTargetListInProgressSelector);

	const isCompaniesSavingToProject = useSelector(savingCompaniesByFilterInProgressSelector);

	const removingCompany = useAppSelector(removingCompanyFromTargetListInProgressSelector);

	const isStarterOrPlusUser: boolean =
		userLicenseTypeName === "Starter" || (userLicenseTypeName === "Plus" && !isSuperUSer);

	const loading = useMemo(
		() =>
			loadingCompanyTargetList ||
			removingCompanies ||
			isCompaniesSavingToProject ||
			loadingTargetLists ||
			addingCompany?.length > 0 ||
			removingCompany?.length > 0,
		[
			loadingCompanyTargetList,
			loadingTargetLists,
			removingCompanies,
			isCompaniesSavingToProject,
			addingCompany?.length,
			removingCompany?.length,
		],
	);

	const handleRemoveAllClick = useCallback(() => {
		customConfirmAlert({
			yesButtonLabel: "Yes",
			noButtonLabel: "No",
			title: "CompanyDetails Deletion Warning!",
			message: `Are you sure about deleting all companies from the Target List?`,
			handlePressYes: () => {
				dispatch(removeCompaniesFromTargetList());
			},
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleAddFromMarketMapClick = useCallback(() => {
		dispatch(addFromMarketMapModal(true, {}));
	}, [dispatch]);

	const handleTrashClick = (event: MouseEvent<SVGElement>, companyID: number) => {
		event.stopPropagation();
		event.preventDefault();

		const company = companies?.find((c: STCompany) => c.id === companyID);

		customConfirmAlert({
			yesButtonLabel: "Yes",
			noButtonLabel: "No",
			title: "CompanyDetails Deletion Warning!",
			message: `Are you sure about deleting "${company.name}" from the Target List?`,
			handlePressYes: () => {
				dispatch(removeCompanyFromTargetList(companyID));
			},
		});
	};

	const handleSeeAllCompaniesClick = useCallback(() => {
		if (project?.id) {
			dispatch(setProjectTabIndex(ProjectTabs.savedCompaniesTabIndex));
			history.push(`/project/${project.id}`);
		}
	}, [dispatch, history, project?.id]);

	const featureUserList: TFeatureProp[] = useMemo(
		() => [
			hasAccessToMarketMaps
				? {
						id: 3,
						deleteBtn: false,
						icon: MarketMapsIcon,
						btnClassName: isStarterOrPlusUser ? styles.starter : "",
						text: "Add from Target Lists",
						onClick: !isStarterOrPlusUser ? handleAddFromMarketMapClick : noop,
				  }
				: {
						id: "EmptyItem",
				  },
			targetCompanyPagination?.count > 50 && project
				? {
						id: 11,
						deleteBtn: false,
						icon: PreviewIcon,
						btnClassName: isStarterOrPlusUser ? styles.starter : "",
						text: "See All Companies",
						onClick: handleSeeAllCompaniesClick,
				  }
				: {
						id: "EmptyItem",
				  },
			Number(!!targetCompanyPagination?.count) === 0
				? {
						id: "EmptyData",
						text: "companies",
				  }
				: {
						id: "EmptyItem",
				  },
			Number(!!targetCompanyPagination?.count) > 0
				? {
						id: 2,
						deleteBtn: true,
						icon: RecycleBinLine,
						text: "Remove All Companies",
						onClick: handleRemoveAllClick,
				  }
				: {id: "EmptyItem"},
		],
		[
			targetCompanyPagination?.count,
			isStarterOrPlusUser,
			handleSeeAllCompaniesClick,
			hasAccessToMarketMaps,
			handleAddFromMarketMapClick,
			handleRemoveAllClick,
			project,
		],
	);

	const displayedCompanies = useMemo(() => {
		return companies.slice(0, 50);
	}, [companies]);

	return (
		<HeaderDropDown
			groupRight
			deleteItem
			onHoverTooltip
			loading={loading}
			pathName="company"
			className={className}
			scrollList={displayedCompanies}
			title={targetCompanyPagination?.count ?? 0}
			LeftIcon={HeaderProjectIcon}
			listClasses={styles.list__width}
			featureUserList={featureUserList}
			handleTrashClick={handleTrashClick}
		/>
	);
};

export default SavedCompany;
